/* Mina Naji shared product predictive search.
 * Uses Shopify's same-origin predictive search endpoint. Full submission stays
 * native: only the direct suggestions are enhanced client-side.
 */
(() => {
  const MINIMUM_QUERY_LENGTH = 2;
  const RESULT_LIMIT = 4;

  const escapeHtml = (value) => String(value || '').replace(/[&<>'"]/g, (character) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;'
  }[character]));

  const formatPrice = (price) => {
    const number = Number.parseFloat(price);
    if (Number.isNaN(number)) return '';
    return new Intl.NumberFormat(document.documentElement.lang || 'nl-NL', {
      style: 'currency', currency: 'EUR', minimumFractionDigits: 2
    }).format(number);
  };

  class MinaSmartSearch {
    constructor(form) {
      this.form = form;
      this.input = form.querySelector('input[name="q"]');
      this.results = form.querySelector('[data-mina-smart-results]');
      this.controller = null;
      this.timer = null;
      this.requestId = 0;

      if (!this.input || !this.results) return;

      this.input.setAttribute('aria-autocomplete', 'list');
      this.input.setAttribute('aria-controls', this.results.id || `mina-smart-search-${Math.random().toString(36).slice(2)}`);
      this.results.id = this.input.getAttribute('aria-controls');
      this.input.addEventListener('input', () => this.onInput());
      this.input.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') this.clear();
      });
      document.addEventListener('click', (event) => {
        if (!this.form.contains(event.target)) this.clear();
      });
    }

    onInput() {
      const query = this.input.value.trim();
      window.clearTimeout(this.timer);
      if (query.length < MINIMUM_QUERY_LENGTH) {
        this.clear();
        return;
      }
      this.timer = window.setTimeout(() => this.search(query), 220);
    }

    async search(query) {
      this.controller?.abort();
      this.controller = new AbortController();
      const requestId = ++this.requestId;
      this.renderStatus('Zoeken…');

      const params = new URLSearchParams({
        q: query,
        'resources[type]': 'product',
        'resources[limit]': String(RESULT_LIMIT),
        'resources[options][prefix]': 'last',
        'resources[options][unavailable_products]': 'last'
      });

      try {
        const response = await fetch(`/search/suggest.json?${params}`, {
          signal: this.controller.signal,
          headers: { Accept: 'application/json' }
        });
        if (!response.ok) throw new Error(`Predictive search failed: ${response.status}`);
        const data = await response.json();
        if (requestId !== this.requestId) return;
        this.renderProducts(data.resources?.results?.products || [], query);
      } catch (error) {
        if (error.name !== 'AbortError') this.clear();
      }
    }

    renderStatus(message) {
      this.results.hidden = false;
      this.results.innerHTML = `<p class="mina-smart-search__status">${escapeHtml(message)}</p>`;
      this.input.setAttribute('aria-expanded', 'true');
    }

    renderProducts(products, query) {
      const allResultsUrl = `${this.form.action}?q=${encodeURIComponent(query)}&type=product&options%5Bprefix%5D=last`;
      const items = products.map((product) => {
        const image = typeof product.image === 'string' ? product.image : product.image?.url;
        const price = formatPrice(product.price);
        return `<a class="mina-smart-search__item" href="${escapeHtml(product.url)}" role="option" data-instant>
          ${image ? `<img src="${escapeHtml(image)}" alt="" width="64" height="64" loading="lazy">` : '<span class="mina-smart-search__image-placeholder" aria-hidden="true"></span>'}
          <span><b>${escapeHtml(product.title)}</b>${product.product_type ? `<small>${escapeHtml(product.product_type)}</small>` : ''}</span>
          ${price ? `<strong>${escapeHtml(price)}</strong>` : ''}
        </a>`;
      }).join('');

      this.results.hidden = false;
      this.results.innerHTML = products.length
        ? `<div class="mina-smart-search__list">${items}</div><a class="mina-smart-search__all" href="${allResultsUrl}">Bekijk alle resultaten <span aria-hidden="true">→</span></a>`
        : `<p class="mina-smart-search__status">Geen producten gevonden voor “${escapeHtml(query)}”.</p><a class="mina-smart-search__all" href="${allResultsUrl}">Zoek verder <span aria-hidden="true">→</span></a>`;
      this.input.setAttribute('aria-expanded', 'true');
    }

    clear() {
      window.clearTimeout(this.timer);
      this.controller?.abort();
      this.results.hidden = true;
      this.results.innerHTML = '';
      this.input?.setAttribute('aria-expanded', 'false');
    }
  }

  const initialiseDrawerSubmit = () => {
    const form = document.querySelector('#search-drawer form[role="search"]');
    const input = form?.querySelector('input[name="q"]');

    if (!form || !input || form.dataset.minaDrawerSubmitReady) return;

    form.dataset.minaDrawerSubmitReady = 'true';
    input.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' || event.isComposing || !input.value.trim()) return;

      event.preventDefault();
      form.requestSubmit();
    });
  };

  const initialise = () => {
    initialiseDrawerSubmit();
    document.querySelectorAll('[data-mina-smart-search]').forEach((form) => {
      if (!form.dataset.minaSmartSearchReady) {
        form.dataset.minaSmartSearchReady = 'true';
        new MinaSmartSearch(form);
      }
    });
  };

  document.addEventListener('shopify:section:load', initialise);
  document.addEventListener('DOMContentLoaded', initialise);
  if (document.readyState !== 'loading') initialise();
})();
